import React, { useMemo, useState } from "react";
import styles from "../Home/HomePage.module.css";

/**
 * TrusteeDashboard
 * ------------------------------------------------------------
 * PRESENTATION-ONLY (dummy data). Keep structure stable so you can
 * later swap in live data without changing the UI.
 *
 * Visuals:
 * - KPI cards (Total Municipal Paid, Recovered, Outstanding/Surplus)
 * - Category cards (Electricity/Water/Sewerage/Refuse) with donut “Recovery %”
 * - Monthly stacked bar (Paid vs Recovered)
 * - Top buildings table (Outstanding focus)
 * - Tiny trend sparkline per category
 *
 * Styling:
 * - Reuses global .container, .card, .btn, .ghost, .h1, .h2, .sub, .badge, .table
 * - Adds a small module for layout niceties
 */

// ---------------------------- Dummy Data ----------------------------
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const DUMMY_MONTHLY = MONTHS.map((m, idx) => {
  const base = 450000 + idx * 12000;
  const jitter = (f) => Math.round(f * (0.9 + (idx%5)*0.025));

  const elecPaid      = jitter(base * 0.42);
  const waterPaid     = jitter(base * 0.28);
  const sewerPaid     = jitter(base * 0.18);
  const refusePaid    = jitter(base * 0.12);

  const elecRecovered   = Math.round(elecPaid   * (0.87 - (idx%4)*0.02));
  const waterRecovered  = Math.round(waterPaid  * (0.82 - (idx%3)*0.02));
  const sewerRecovered  = Math.round(sewerPaid  * (0.80 - (idx%2)*0.015));
  const refuseRecovered = Math.round(refusePaid * (0.79 - (idx%5)*0.01));

  return {
    month: m,
    electricity: { paid: elecPaid, recovered: elecRecovered },
    water:       { paid: waterPaid, recovered: waterRecovered },
    sewerage:    { paid: sewerPaid, recovered: sewerRecovered },
    refuse:      { paid: refusePaid, recovered: refuseRecovered },
  };
});

const DUMMY_BUILDINGS = [
  { name: "Test Building 1", muniAccount: "AC-10492", electricity: 182_000, water: 96_800, sewerage: 38_000, refuse: 15_400 },
  { name: "Test Building 2", muniAccount: "AC-11751", electricity: 145_900, water: 74_600, sewerage: 30_700, refuse: 11_300 },
  { name: "Test Building 3", muniAccount: "AC-12088", electricity: 121_400, water: 69_200, sewerage: 21_900, refuse: 8_700  },
  { name: "Test Building 4", muniAccount: "AC-09133", electricity: 99_300,  water: 55_000, sewerage: 20_400, refuse: 7_900  },
  { name: "Test Building 5", muniAccount: "AC-13207", electricity: 88_000,  water: 49_500, sewerage: 19_000, refuse: 6_600  },
];

// ---------------------------- Helpers ----------------------------
const sumCategory = (arr, cat, key) => arr.reduce((a, m) => a + (m[cat]?.[key] || 0), 0);
const rand = (n) => Math.max(0, Math.round(n + (Math.random() - 0.5) * n * 0.06));
const currency = (n) => "R" + (Number(n || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 });
const pct = (a, b) => (b > 0 ? Math.max(0, Math.min(100, (a / b) * 100)) : 0);
const makeSpark = (arr, cat) => arr.map(m => m[cat].recovered);

/**
 * Recovery tones:
 *  - under: rawPct < 95                 (RED)
 *  - good:  100 <= rawPct <= 110        (GREEN)
 *  - over:  rawPct > 110                (AMBER)
 *  - normal: 95..100                    (neutral)
 */
function getRecoveryInfo(recovered, paid){
  const rawPct = paid > 0 ? (recovered / paid) * 100 : 0;
  const drawPct = Math.max(0, Math.min(100, rawPct));

  let tone = "normal";
  if (rawPct < 95) tone = "under";
  else if (rawPct > 110) tone = "over";
  else if (rawPct >= 100 && rawPct <= 110) tone = "good";
  else tone = "normal";

  const paletteMap = {
    normal: { cardBg: undefined, cardBr: undefined, donut: "#2563eb", badgeBg: "#e5edff", badgeFg: "#1e40af" },
    under:  { cardBg: "#fee2e2", cardBr: "#ef4444", donut: "#ef4444", badgeBg: "#fee2e2", badgeFg: "#991b1b" },
    good:   { cardBg: "#ecfdf5", cardBr: "#10b981", donut: "#10b981", badgeBg: "#d1fae5", badgeFg: "#065f46" },
    over:   { cardBg: "#fff7ed", cardBr: "#f59e0b", donut: "#f59e0b", badgeBg: "#fef3c7", badgeFg: "#92400e" },
  };
  const palette = paletteMap[tone];

  const cardStyle = {
    background: palette.cardBg,
    border: palette.cardBr ? `1px solid ${palette.cardBr}` : undefined,
  };
  const badgeStyle = {
    background: palette.badgeBg,
    color: palette.badgeFg,
    borderRadius: 999,
    padding: "2px 8px",
    fontWeight: 600,
    fontSize: 12,
  };

  return { rawPct, drawPct, tone, cardStyle, badgeStyle, donutColor: palette.donut };
}

// ---------------------------- Charts ----------------------------
function Donut({ value, total, size=120, stroke=14, label="Recovery", color }) {
  const radius = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const C = 2 * Math.PI * radius;

  const rawPct = total > 0 ? (value / total) * 100 : 0;
  const drawPct = Math.max(0, Math.min(100, rawPct));
  const dash = (drawPct / 100) * C;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className={styles.donut}>
      <circle cx={cx} cy={cy} r={radius} strokeWidth={stroke} className={styles.donutTrack} />
      <circle
        cx={cx} cy={cy} r={radius} strokeWidth={stroke}
        className={styles.donutFill}
        stroke={color || "#2563eb"}
        strokeDasharray={`${dash} ${C - dash}`}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
      <text x={cx} y={cy - 4} textAnchor="middle" className={styles.donutBig}>
        {Math.round(rawPct)}%
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" className={styles.donutSub}>
        {label}
      </text>
    </svg>
  );
}

function StackedBarMonthly({ rows, height=260, padding=30 }) {
  if (!rows?.length) return null;
  const svgWidth = 980;
  const svgHeight = height;
  const w = svgWidth - padding * 2;
  const h = svgHeight - padding * 2;

  const totals = rows.map(r => {
    const paid = r.electricity.paid + r.water.paid + r.sewerage.paid + r.refuse.paid;
    const rec  = r.electricity.recovered + r.water.recovered + r.sewerage.recovered + r.refuse.recovered;
    return { paid, rec };
  });

  const maxY = Math.max(...totals.map(t => Math.max(t.paid, t.rec))) || 1;
  const barW = w / rows.length * 0.76;
  const gap = (w / rows.length) - barW;

  const sx = (i) => padding + i * (barW + gap) + gap / 2;
  const sy = (v) => padding + (1 - v / maxY) * h;
  const hFor = (v) => Math.max(0, sy(0) - sy(v));

  const yticks = [0, maxY / 2, maxY];

  return (
    <div className={styles.chartWrap}>
      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width:"100%", height: svgHeight }}>
        <line x1={padding} y1={svgHeight - padding} x2={svgWidth - padding} y2={svgHeight - padding} stroke="#e5e7eb" />
        <line x1={padding} y1={padding} x2={padding} y2={svgHeight - padding} stroke="#e5e7eb" />

        {yticks.map((v, i) => (
          <g key={i}>
            <line x1={padding} y1={sy(v)} x2={svgWidth - padding} y2={sy(v)} stroke="#f3f4f6" />
            <text x={padding - 8} y={sy(v)} textAnchor="end" alignmentBaseline="middle" fontSize="10" fill="#6b7280">
              {currency(v)}
            </text>
          </g>
        ))}

        {rows.map((r, i) => {
          const x = sx(i);
          const paid = totals[i].paid;
          const rec  = totals[i].rec;
          const paidH = hFor(paid);
          const recH  = hFor(rec);
          const baseY = sy(paid);
          const recY  = sy(rec);
          return (
            <g key={r.month}>
              <rect x={x} y={baseY} width={barW} height={paidH} rx="6" fill="#c7d2fe" />
              <rect x={x} y={recY} width={barW} height={recH} rx="6" fill="#2563eb" opacity="0.9" />
              <text x={x + barW / 2} y={svgHeight - padding + 14} fontSize="10" textAnchor="middle" fill="#6b7280">{r.month}</text>
            </g>
          );
        })}
      </svg>

      <div className={styles.legendRow}>
        <span className={styles.legendItem}><i style={{background:"#2563eb"}} /> Recovered</span>
        <span className={styles.legendItem}><i style={{background:"#c7d2fe"}} /> Paid</span>
      </div>
    </div>
  );
}

function Sparkline({ values, height=40, padding=4 }) {
  if (!values?.length) return null;
  const svgWidth = 160;
  const svgHeight = height;
  const w = svgWidth - padding*2;
  const h = svgHeight - padding*2;

  const xs = values.map((_, i) => i);
  const minY = Math.min(...values);
  const maxY = Math.max(...values);
  const sx = (i) => padding + (i / (values.length - 1)) * w;
  const sy = (v) => padding + (1 - (v - minY) / (maxY - minY || 1)) * h;

  const d = values.map((v, i) => `${i ? "L" : "M"} ${sx(i)} ${sy(v)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className={styles.spark}>
      <path d={d} fill="none" stroke="#2563eb" strokeWidth="2" />
      <circle cx={sx(values.length - 1)} cy={sy(values.at(-1))} r="3" fill="#2563eb" />
    </svg>
  );
}

// ---------------------------- Main Component ----------------------------
export default function TrusteeDashboard(){
  const [year] = useState(new Date().getFullYear());
  const [building, setBuilding] = useState("All buildings");
  const [view, setView] = useState("all");

  // Real totals from dummy data (KPIs use this)
  const totals = useMemo(() => {
    const cats = ["electricity", "water", "sewerage", "refuse"];
    const out = {};
    cats.forEach(c => {
      const paid = sumCategory(DUMMY_MONTHLY, c, "paid");
      const rec  = sumCategory(DUMMY_MONTHLY, c, "recovered");
      const info = getRecoveryInfo(rec, paid);
      out[c] = {
        paid,
        recovered: rec,
        outstanding: Math.max(0, paid - rec),
        surplus: paid - rec < 0 ? Math.abs(paid - rec) : 0,
        recoveryPct: info.rawPct,
        _tone: info.tone,
      };
    });
    const paidAll = Object.values(out).reduce((a, c) => a + c.paid, 0);
    //const recAll  = Object.values(out).reduce((a, c) => a + c.recovered, 0);
    const recAll  = 6000000;
    const infoAll = getRecoveryInfo(recAll, paidAll);
    return {
      byCategory: out,
      allPaid: paidAll,
      allRecovered: recAll,
      allOutstanding: Math.max(0, paidAll - recAll),
      allSurplus: paidAll - recAll < 0 ? Math.abs(paidAll - recAll) : 0,
      allRecoveryPct: infoAll.rawPct,
      _toneAll: infoAll.tone,
    };
  }, []);

  // PRESENTATION OVERRIDES for category donuts only (examples of each tone)
  // Electricity: under (~90%), Water: good (~105%), Sewerage: over (~120%), Refuse: normal (~98%)
  const demoMultipliers = {
    electricity: 1.02,
    water: 1.05,
    sewerage: 1.20,
    refuse: 0.98,
  };

  const demoTotals = useMemo(() => {
    const out = {};
    Object.entries(totals.byCategory).forEach(([key, base]) => {
      const mult = demoMultipliers[key] ?? 1;
      const demoRecovered = Math.round(base.paid * mult);
      const info = getRecoveryInfo(demoRecovered, base.paid);
      out[key] = {
        paid: base.paid,
        recovered: demoRecovered,
        outstanding: Math.max(0, base.paid - demoRecovered),
        surplus: base.paid - demoRecovered < 0 ? Math.abs(base.paid - demoRecovered) : 0,
        recoveryPct: info.rawPct,
        _tone: info.tone,
        _info: info,
      };
    });
    return out;
  }, [totals.byCategory]);

  const categoryCards = [
    { key: "electricity", title: "Electricity" },
    { key: "water",       title: "Water" },
    { key: "sewerage",    title: "Sewerage" },
    { key: "refuse",      title: "Refuse" },
  ];

  return (
  <div className={styles.page}>
    <div className={`${styles.section} ${styles.trusteeDashSection}`}>
      <div className={styles.meterDetailHero}>
        <div>
          <p className={styles.sectionEyebrow}>Trustee Portal</p>
          <h1 className={styles.h2}>Dashboard</h1>
          <p className={styles.sectionIntro}>
            High-level view of municipal spend vs recovery across your portfolio.
          </p>
        </div>
      </div>

      <div className={styles.trusteeDashToolbar}>
        <select className={styles.authInput} value={building} onChange={(e) => setBuilding(e.target.value)}>
          <option>All buildings</option>
          <option>Test Building 1</option>
          <option>Test Building 2</option>
          <option>Test Building 3</option>
          <option>Test Building 4</option>
          <option>Test Building 5</option>
        </select>

        <select className={styles.authInput} value={view} onChange={(e) => setView(e.target.value)}>
          <option value="all">All categories</option>
          <option value="electricity">Electricity</option>
          <option value="water">Water</option>
          <option value="sewerage">Sewerage</option>
          <option value="refuse">Refuse</option>
        </select>

        <button className={styles.inlineBtn}>Export</button>
        <button className={styles.primaryBtn}>Create alert</button>
      </div>

      <div className={styles.meterDetailGrid}>
        <div className={styles.meterDetailCard}>
          <h3>Total Municipal Paid</h3>
          <p>{currency(totals.allPaid)}</p>
          <span>FY {year}</span>
        </div>

        <div className={styles.meterDetailCard}>
          <h3>Total Recovered</h3>
          <p>{currency(totals.allRecovered)}</p>
          <span>Recovery {Math.round(totals.allRecoveryPct)}%</span>
        </div>

        <div className={styles.meterDetailCard}>
          <h3>{totals.allOutstanding > 0 ? "Outstanding" : "Surplus"}</h3>
          <p>
            {totals.allOutstanding > 0
              ? currency(totals.allOutstanding)
              : currency(totals.allSurplus)}
          </p>
        </div>
      </div>

      <div className={styles.trusteeCategoryGrid}>
        {categoryCards.map((c) => {
          const t = demoTotals[c.key];
          const info = t._info;

          return (
            <div className={styles.trusteeCategoryCard} key={c.key}>
              <div className={styles.cardHeader}>
                <strong>{c.title}</strong>
                <span className={styles.badge}>{Math.round(info.rawPct)}%</span>
              </div>

              <div className={styles.bigValue}>{currency(t.paid)}</div>
              <div className={styles.micro}>Recovered {currency(t.recovered)}</div>

              <div className={styles.donutWrap}>
                <Donut value={t.recovered} total={t.paid} label="Recovery" color={info.donutColor} />
              </div>

              <Sparkline values={makeSpark(DUMMY_MONTHLY, c.key)} />
            </div>
          );
        })}
      </div>

      <div className={styles.meterDetailPanel}>
        <div className={styles.meterDetailPanelHeader}>
          <div>
            <p className={styles.sectionEyebrow}>Recovery</p>
            <h2>Monthly Paid vs Recovered</h2>
          </div>
        </div>

        <div className={styles.meterChartCard}>
          <StackedBarMonthly rows={DUMMY_MONTHLY} />
        </div>
      </div>

      <div className={styles.meterDetailPanel}>
        <div className={styles.meterDetailPanelHeader}>
          <div>
            <p className={styles.sectionEyebrow}>Portfolio</p>
            <h2>Buildings</h2>
          </div>
        </div>

        <div className={styles.meterReadingsTableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Building</th>
                <th>Account</th>
                <th>Electricity</th>
                <th>Water</th>
                <th>Sewerage</th>
                <th>Refuse</th>
                <th>Total</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {[...DUMMY_BUILDINGS]
                .map((b) => ({
                  ...b,
                  total: b.electricity + b.water + b.sewerage + b.refuse,
                }))
                .sort((a, b) => b.total - a.total)
                .map((b) => (
                  <tr key={b.name}>
                    <td>{b.name}</td>
                    <td>{b.muniAccount}</td>
                    <td>{currency(b.electricity)}</td>
                    <td>{currency(b.water)}</td>
                    <td>{currency(b.sewerage)}</td>
                    <td>{currency(b.refuse)}</td>
                    <td><strong>{currency(b.total)}</strong></td>
                    <td><button className={styles.inlineBtn}>View</button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);
}
